/**
 * Motor de resolución de couriers por destino (Logistics Routing Engine).
 * Dado tenant + departamento/provincia/distrito devuelve couriers disponibles y opcionalmente el preseleccionado.
 * Plan: docs/plan/PLAN-LOGISTICA-AVANZADA.md
 */

import { supabase } from './supabase';
import { devLog } from './utils';
/*
 * 🚨 EL NOMBRE DE UN LUGAR ES UNA CLAVE, Y ACÁ SE COMPARABA COMO TEXTO LIBRE (16-sep-2026). Un
 *    pedido a Los Baños del Inca no ofrecía Shalom: el pedido decía «Cajamarca», la cobertura
 *    «CAJAMARCA», y este archivo comparaba con `department.eq.…` y siete `===`. Con la mayúscula
 *    no coincidía nadie, Shalom caía en «declaró cobertura y acá no llega», y se escondía en todo
 *    destino fuera de Lima, Cusco y Arequipa.
 *
 * ⭐ AHORA HAY UNA SOLA CLAVE, y el criterio vive en `@/lib/ubigeo` junto al normalizador, para que
 *    el resolver, el editor de cobertura y cualquier otro comparen igual. Se calcula una vez por
 *    destino y TODAS las comparaciones son contra ella. Las tablas de cobertura y reglas son
 *    chicas (79 filas de cobertura en total): se traen sin filtrar por departamento y se filtran
 *    acá. De paso desaparece el `.or(department.eq.${texto})`, que además rompía si el nombre
 *    traía una coma o un paréntesis. La historia completa está en `ubigeo.ts`.
 */
import { claveDeLugar, mismoLugar } from './ubigeo';
// El mismo criterio de cobertura que usa Entrega para dejar registrar: lo que se ofrece y lo que se registra no se contradicen.
import { filaAlcanzaElDestino } from './logistics/laCoberturaDelCourier';

/** Ray-casting: punto (pointLat, pointLng) dentro de ring. Ring como [[lat, lng], ...]. */
export function pointInPolygon(
  pointLat: number,
  pointLng: number,
  ring: Array<[number, number]> | number[][]
): boolean {
  if (!ring || ring.length < 3) return false;
  const x = pointLng;
  const y = pointLat;
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const yi = ring[i][0];
    const xi = ring[i][1];
    const yj = ring[j][0];
    const xj = ring[j][1];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Normaliza polígono desde DB: array [[lat,lng],...] o GeoJSON { type, coordinates: [[lng,lat],...] }. Devuelve [[lat,lng],...]. */
export function normalizePolygonRing(geojson: unknown): Array<[number, number]> | null {
  if (!geojson) return null;
  let ring: number[][];
  if (Array.isArray(geojson)) {
    ring = geojson as number[][];
  } else if (typeof geojson === 'object' && geojson !== null && 'coordinates' in geojson) {
    const coords = (geojson as { coordinates: number[][][] }).coordinates;
    // FIX 2026-06-16: era `Array.isArray(coords)?.[0]` → boolean?.[0] = undefined →
    // ring SIEMPRE [] → los polígonos GeoJSON nunca se parseaban (zona rota).
    ring = Array.isArray(coords) && Array.isArray(coords[0]) ? coords[0] : [];
    if (ring.length >= 3) {
      return ring.map((c) => [Number(c[1]), Number(c[0])] as [number, number]);
    }
    return null;
  } else {
    return null;
  }
  if (ring.length < 3) return null;
  const first = ring[0];
  if (!Array.isArray(first) || first.length < 2) return null;
  return ring.map((c) => [Number(c[0]), Number(c[1])] as [number, number]);
}

export interface LogisticsDestination {
  department: string;
  province?: string | null;
  district?: string | null;
}

export interface AvailableCourierOption {
  courier_service_id: string;
  courier_id: string;
  courier_name: string;
  courier_type: string;
  service_name: string;
  delivery_type: string;
  priority: number;
  is_auto_assign: boolean;
  logo_url: string | null;
  brand_color: string | null;
  /** Se ve pero no se elige (un courier de Drop en una venta por agencia, o fuera de su cobertura). */
  disabled?: boolean;
  /** Un courier de la red de Smarty (20261028150000): lo que Smarty le descuenta al negocio por el envío. */
  envio_de_la_red?: number | null;
  /** La zona roja del courier donde cae el pin (20261029010000): se elige, pero entrega coordinando. */
  coordinar?: string | null;
}

export interface ResolveCouriersResult {
  options: AvailableCourierOption[];
  recommended_service_id: string | null;
}

/**
 * Joins to-one de logistics_shipping_rules (FK courier_service_id → servicio; FK courier_id → courier).
 * El cliente Supabase corre sin tipos generados y su parser infiere toda relación embebida como
 * ARRAY, pero en runtime estas FKs son to-one y PostgREST devuelve OBJETO (o null).
 * `.overrideTypes<..., { merge: false }>()` declara ese shape real en la propia consulta.
 */
interface CourierServiceJoin {
  id: string;
  name: string;
  delivery_type: string;
  courier_id: string;
  logistics_couriers: { id: string; name: string; type: string; logo_url: string | null; brand_color: string | null } | null;
}

interface ShippingRuleResolverRow {
  id: string;
  courier_service_id: string;
  priority: number;
  is_auto_assign: boolean;
  department: string | null;
  province: string | null;
  district: string | null;
  logistics_courier_services: CourierServiceJoin | null;
}

/** Variante para resolveCouriersForPoint: mismo join, sin columnas de zona en el select. */
type ShippingRulePointRow = Omit<ShippingRuleResolverRow, 'department' | 'province' | 'district'>;

const CACHE_TTL_MS = 60_000;
const CACHE_MAX_ENTRIES = 100;
const destinationCache = new Map<
  string,
  { result: ResolveCouriersResult; expiresAt: number }
>();

function cacheKey(tenantId: string, d: LogisticsDestination): string {
  return `${tenantId}:${d.department ?? ''}:${d.province ?? ''}:${d.district ?? ''}`;
}

function getCached(tenantId: string, d: LogisticsDestination): ResolveCouriersResult | null {
  const key = cacheKey(tenantId, d);
  const entry = destinationCache.get(key);
  if (!entry || Date.now() > entry.expiresAt) {
    if (entry) destinationCache.delete(key);
    return null;
  }
  return entry.result;
}

/**
 * Vacía la caché de destinos.
 *
 * 🪤 SIN ESTO EL INTERRUPTOR PARECE ROTO DURANTE UN MINUTO. La caché vive 60 s, así que apagar una
 *    zona y volver al selector de envío seguía mostrando el courier — y un control que tarda en
 *    obedecer se lee como uno que no funciona. La llama la pantalla de zonas al cambiar el estado.
 */
export function limpiarCacheDeCouriers(): void {
  destinationCache.clear();
}

function setCached(tenantId: string, d: LogisticsDestination, result: ResolveCouriersResult): void {
  if (destinationCache.size >= CACHE_MAX_ENTRIES) {
    const firstKey = destinationCache.keys().next().value;
    if (firstKey != null) destinationCache.delete(firstKey);
  }
  const key = cacheKey(tenantId, d);
  destinationCache.set(key, { result, expiresAt: Date.now() + CACHE_TTL_MS });
}

/** Specificity score: regla más específica gana (district +4, province +2, department +1). */
function ruleSpecificity(
  r: { department: string | null; province: string | null; district: string | null },
  normDept: string,
  normProv: string | null,
  normDist: string | null
): number {
  let score = 0;
  // 🪤 Los tres parámetros ya vienen como CLAVE (ver `claveDeLugar`); la fila se normaliza acá.
  if (r.department != null && claveDeLugar(r.department) === normDept) score += 1;
  if (r.province != null && claveDeLugar(r.province) === normProv) score += 2;
  if (r.district != null && claveDeLugar(r.district) === normDist) score += 4;
  return score;
}

export interface ZonaDeCourier {
  logistics_courier_id: string | null;
  is_active: boolean | null;
}

/**
 * Qué couriers apagó el comercio EN ESTE DISTRITO, mirando sus zonas.
 *
 * 🪤 SÓLO SI TODAS SUS ZONAS DEL DISTRITO ESTÁN APAGADAS. Un distrito puede tener varios polígonos
 *    del mismo courier —Ate tiene tres en el mapa de Eva— y apagar uno significa «acá no» para ese
 *    pedazo, no «este courier no atiende Ate». Confundirlo dejaría al comerciante sin el courier por
 *    haber apagado un polígono que ni recuerda.
 *
 * 🪤 Y SIN ZONAS CARGADAS NO SE APAGA NADA. La ausencia de zonas no es una decisión del comerciante:
 *    es un courier del que todavía no tenemos el mapa. Tratarla como «apagado» escondería couriers
 *    que sí atienden.
 */
export function couriersApagadosEnElDistrito(zonas: ZonaDeCourier[]): string[] {
  const porCourier = new Map<string, { total: number; activas: number }>();
  for (const z of zonas) {
    if (!z.logistics_courier_id) continue;
    const acc = porCourier.get(z.logistics_courier_id) ?? { total: 0, activas: 0 };
    acc.total += 1;
    if (z.is_active !== false) acc.activas += 1;
    porCourier.set(z.logistics_courier_id, acc);
  }
  const apagados: string[] = [];
  for (const [courierId, c] of porCourier) {
    if (c.total > 0 && c.activas === 0) apagados.push(courierId);
  }
  return apagados;
}

/**
 * El transporte «lo llevo yo». Vive en `logistics_couriers` como cualquier otro (type `propia`),
 * asi que hereda el guardado del envio, los estados, el rotulo y las listas — sin casos especiales.
 *
 * 🪤 Se cachea en memoria porque es una fila que no cambia: pedirla en cada resolucion seria un
 *    viaje por destino, y el resolvedor ya se llama en cada apertura del selector.
 */
let _repartoPropio: AvailableCourierOption | null | undefined;
async function cargarRepartoPropio(): Promise<AvailableCourierOption | null> {
  if (_repartoPropio !== undefined) return _repartoPropio;
  try {
    const { data } = await supabase
      .from('logistics_couriers')
      .select('id, name, logo_url, brand_color')
      .eq('type', 'propia')
      .limit(1)
      .maybeSingle();
    _repartoPropio = data
      ? {
          courier_service_id: `propia:${data.id}`,
          courier_id: data.id as string,
          courier_name: (data.name as string) || 'Reparto propio',
          courier_type: 'propia',
          service_name: (data.name as string) || 'Reparto propio',
          delivery_type: 'domicilio',
          priority: 999,
          is_auto_assign: false,
          logo_url: (data.logo_url as string | null) ?? null,
          brand_color: (data.brand_color as string | null) ?? null,
        }
      : null;
  } catch {
    _repartoPropio = null; // sin el, el selector sigue mostrando los couriers de siempre
  }
  return _repartoPropio;
}
/**
 * Determina qué couriers puede usar el negocio para un destino.
 * 1) Sus reglas (logistics_shipping_rules) que alcanzan el destino y llegan: van primero, la más específica gana,
 *    y la que tiene is_auto_assign es la recomendada.
 * 2) Después, los couriers de la plataforma y los del negocio que llegan al destino.
 * 3) Fuera los que el negocio apagó (en general o en las zonas del distrito); al final, el reparto propio.
 */
export async function resolveCouriersForDestination(
  tenantId: string,
  destination: LogisticsDestination
): Promise<ResolveCouriersResult> {
  const { department, province = null, district = null } = destination;
  // ⭐ La clave del destino, calculada UNA vez. Todo lo que sigue compara contra esto.
  const normDept = claveDeLugar(department) ?? '';
  const normProv = claveDeLugar(province);
  const normDist = claveDeLugar(district);

  /*
   * ⭐ SIN DESTINO TODAVÍA SE ELIGE IGUAL (founder, 3-oct: «no me deja asignar un envío sin haber digitado primero
   *    el departamento, provincia y distrito… eso ya se había corregido en los drops»). Acá se devolvía la lista
   *    vacía. Es lo mismo que Smarty Drop (20261002200000): un destino que todavía no se sabe no está fuera de
   *    ninguna cobertura. Se ofrece lo que el negocio puede usar, y la cobertura la revisa el registro en Entrega,
   *    que pide la dirección completa.
   */
  const sinDestino = !normDept;

  const destKey: LogisticsDestination = { department: normDept, province: normProv, district: normDist };
  const cached = getCached(tenantId, destKey);
  if (cached) return cached;

  /*
   * ⭐ TODAS LAS CONSULTAS EN UNA SOLA OLA. La de las zonas es la que hace real su interruptor.
   *
   *    «Eva llega a Surco» y «yo no quiero usar Eva en Surco» son dos cosas distintas: la primera
   *    es un hecho del courier y vive en `logistics_courier_coverage`, global para todos los
   *    comercios; la segunda es una preferencia de ESTE comercio y vive en las zonas que puede
   *    apagar. Hasta acá sólo mandaba la primera, así que apagar una zona cambiaba el mapa y nada
   *    más — el botón prometía un control que no ejercía.
   */
  const [covRes, rulesRes, disabledRes, declaranRes, zonasRes, couriersRes] = await Promise.all([
    supabase
      .from('logistics_courier_coverage')
      /* 🪤 `courier_service_id` VIENE TAMBIÉN: la cobertura puede ser de UN servicio del courier y no
         de todos. Sin eso, un courier con moto local y agencia nacional ofrece la moto en provincia. */
      /* 🪤 SIN FILTRO POR DEPARTAMENTO EN EL SERVIDOR, A PROPÓSITO. `department.eq.Cajamarca` no
         encontraba «CAJAMARCA». La tabla es global y chica (decenas de filas): se trae entera y se
         filtra abajo con la clave normalizada. Ver el bloque de arriba. */
      .select('courier_id, courier_service_id, department, province, district'),
    supabase
      .from('logistics_shipping_rules')
      .select(`
        id, courier_service_id, priority, is_auto_assign, department, province, district,
        logistics_courier_services (
          id, name, delivery_type, courier_id,
          logistics_couriers ( id, name, type, logo_url, brand_color )
        )
      `)
      .eq('tenant_id', tenantId)
      .eq('enabled', true)
      // Mismo motivo: el departamento se filtra en memoria con `matchDepartment`, por clave.
      .overrideTypes<ShippingRuleResolverRow[], { merge: false }>(),
    supabase
      .from('logistics_courier_settings')
      .select('courier_id')
      .eq('tenant_id', tenantId)
      .eq('enabled', false),
    /*
     * 🚨 QUIÉN DECLARA COBERTURA, EN GENERAL. No alcanza con la del destino: hace falta distinguir
     * «este courier declaró dónde llega y acá no está» de «este courier no declaró nada». Al
     * primero no se lo ofrece; al segundo sí, porque no se le inventa un límite que no puso.
     */
    supabase.from('logistics_courier_coverage').select('courier_id'),
    // Zonas de courier del comercio para este distrito, con su interruptor.
    // 🪤 El distrito también se filtra en memoria por clave: «Los Baños del Inca» y «LOS BANOS DEL
    //    INCA» son el mismo lugar. Se traen las zonas de courier del comercio y se recorta abajo.
    normDist
      ? supabase
          .from('ecommerce_delivery_districts')
          .select('logistics_courier_id, is_active, district_name')
          .eq('tenant_id', tenantId)
          .not('logistics_courier_id', 'is', null)
      : Promise.resolve({ data: [], error: null }),
    // Los couriers de la plataforma y los de este comercio (ver `aQuienesSeLesPregunta`), en la misma ola.
    supabase
      .from('logistics_couriers')
      .select(`
        id, name, type, logo_url, brand_color,
        logistics_courier_services ( id, name, delivery_type )
      `)
      .or(aQuienesSeLesPregunta(tenantId))
      .overrideTypes<CourierDelCatalogo[], { merge: false }>(),
  ]);

  if (covRes.error) {
    devLog.warn('[logistics-resolver] Error cobertura:', covRes.error);
    return { options: [], recommended_service_id: null };
  }
  if (rulesRes.error) {
    devLog.warn('[logistics-resolver] Error reglas:', rulesRes.error);
    return { options: [], recommended_service_id: null };
  }

  // Couriers desactivados por el tenant (cargados arriba; se filtran en memoria, sin 2ª consulta).
  const disabledIds = new Set((disabledRes.data ?? []).map((r: { courier_id: string }) => r.courier_id));

  /*
   * 🪤 SE APAGA SÓLO SI TODAS SUS ZONAS DEL DISTRITO ESTÁN APAGADAS. Un distrito puede tener varios
   *    polígonos del mismo courier (Ate tiene tres en el mapa de Eva). Apagar uno significa «acá no»
   *    para ese pedazo, no «este courier no atiende Ate». Confundirlo dejaría al comerciante sin el
   *    courier por haber apagado un polígono que ni recuerda.
   *
   * 🪤 Y SI NO TIENE NINGUNA ZONA CARGADA, NO SE APAGA NADA. La ausencia de zonas no es una
   *    decisión: es un courier del que todavía no tenemos el mapa.
   */
  // Sólo las zonas de ESTE distrito, comparando por clave (la consulta trajo las del comercio).
  const zonasDelDistrito = ((zonasRes as { data?: Array<ZonaDeCourier & { district_name?: string | null }> }).data ?? [])
    .filter((z) => claveDeLugar(z.district_name) === normDist);
  for (const id of couriersApagadosEnElDistrito(zonasDelDistrito)) disabledIds.add(id);

  /*
   * ⭐ UN SOLO CRITERIO DE COINCIDENCIA PARA TODO. `null` en la fila significa «cualquiera»; si hay
   *    valor, se compara por clave. Antes eran siete comparaciones `===` sueltas, y bastó que una
   *    fila viniera en mayúsculas para que ninguna coincidiera.
   */
  const matchDepartment = (r: { department?: string | null }) => mismoLugar(r.department, normDept);
  const matchProvince = (r: { province?: string | null }) => mismoLugar(r.province, normProv);
  const matchDistrict = (r: { district?: string | null }) => mismoLugar(r.district, normDist);

  // La cobertura ya no viene filtrada del servidor: acá se queda sólo la que alcanza este destino.
  const coverageFiltered = (covRes.data || []).filter(
    (row: { department?: string | null; province?: string | null; district?: string | null; courier_id: string; courier_service_id?: string | null }) =>
      filaAlcanzaElDestino(row, { dept: normDept, prov: normProv, dist: normDist }),
  );

  const alcanceDelDestino = cubrenEsteDestino(coverageFiltered);

  // Idem las reglas: se trajeron todas las habilitadas del comercio, y el departamento se decide acá.
  const rules = (rulesRes.data || []).filter((r) => sinDestino || matchDepartment(r));

  /*
   * 🪤 Y LA COBERTURA SE MIRA POR SERVICIO, no sólo por courier. Una regla que apunta al motorizado
   * de un courier cuya cobertura acá es sólo de su agencia NO puede pasar: es el mismo defecto que
   * el respaldo tenía, por la otra puerta.
   */
  const llegaEsteServicio = (r: { courier_service_id: string; logistics_courier_services?: { courier_id?: string } | null }) => (
    alcanceDelDestino.has(`servicio:${r.courier_service_id}`)
    || alcanceDelDestino.has(`courier:${r.logistics_courier_services?.courier_id ?? ''}`)
  );

  const rulesMatchingDestination = sinDestino ? rules : rules.filter(
    (r) => matchDepartment(r) && matchProvince(r) && matchDistrict(r) && llegaEsteServicio(r)
  );

  // Deduplicar por courier_service_id: la regla más específica gana
  const bestByService = new Map<string, typeof rulesMatchingDestination[0] & { _specificity: number }>();
  for (const r of rulesMatchingDestination) {
    const spec = ruleSpecificity(r, normDept, normProv, normDist);
    const existing = bestByService.get(r.courier_service_id);
    if (!existing || spec > existing._specificity || (spec === existing._specificity && r.priority < existing.priority)) {
      bestByService.set(r.courier_service_id, { ...r, _specificity: spec });
    }
  }

  let options: AvailableCourierOption[] = [...bestByService.values()]
    .sort((a, b) => b._specificity - a._specificity || a.priority - b.priority)
    .map((r) => {
      const svc = r.logistics_courier_services;
      const courier = svc?.logistics_couriers;
      return {
        courier_service_id: r.courier_service_id,
        courier_id: svc?.courier_id ?? '',
        courier_name: courier?.name ?? 'Courier',
        courier_type: courier?.type ?? 'agency',
        service_name: svc?.name ?? '',
        delivery_type: svc?.delivery_type ?? 'agency',
        priority: r.priority,
        is_auto_assign: r.is_auto_assign,
        logo_url: courier?.logo_url ?? null,
        brand_color: courier?.brand_color ?? null,
      };
    })
    .filter((o) => o.courier_id);

  // Excluir couriers desactivados por el tenant (ya cargados arriba; sin consulta extra).
  options = options.filter((o) => !disabledIds.has(o.courier_id));

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * ⭐ LAS REGLAS ORDENAN, NO ESCONDEN
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * El founder (3-oct): «ni en Vitalex me deja usar Eva». Los couriers de la plataforma sólo se
   * ofrecían si el negocio NO tenía ninguna regla para el destino. Vitalex tiene una —InDrive en
   * Lima— y con eso en Lima veía InDrive y nada más: ni Eva, ni Shalom, ni Olva. A Dulti le pasaba
   * igual con InDrive y NorthExpress, y perdía hasta a Nómades, su propio repartidor.
   *
   * Una regla dice cuál prefiere el negocio y cuál se elige solo; no apaga a los demás. Para eso
   * está el interruptor del courier (logistics_courier_settings.enabled = false), que se sigue
   * respetando. Las reglas van primero y los de la plataforma después, sin repetir un courier.
   *
   * 🚨 Y LOS DE LA PLATAFORMA PASAN POR SU COBERTURA. Antes el respaldo devolvía los ocho couriers
   * del sistema para cualquier destino: a un pedido a Arequipa le ofrecía «Nómades Lurín» y a Eva,
   * que cubre 47 distritos de Lima. Elegir uno de ésos deja el paquete sin salir.
   *
   * 🪤 UN COURIER SIN COBERTURA DECLARADA SE SIGUE OFRECIENDO. «No declaró dónde llega» no es
   * «no llega a ningún lado»: inventarle un límite que nadie puso sacaría de la lista a couriers
   * que hoy se usan. Sólo se esconde al que declaró su alcance y este destino no está en él.
   */
  const losDeLaPlataforma = losQueLleganAlDestino(couriersRes.data ?? [], disabledIds, sinDestino ? null : {
    cubiertos: alcanceDelDestino,
    declaran: new Set(((declaranRes as { data?: Array<{ courier_id: string }> }).data ?? [])
      .map((r) => r.courier_id)),
  });
  const conRegla = new Set(options.map((o) => o.courier_id));
  options = [...options, ...losDeLaPlataforma.filter((o) => !conRegla.has(o.courier_id))];

  /*
   * ⭐ EL REPARTO PROPIO SE OFRECE SIEMPRE, sin mirar el destino ni las reglas del tenant.
   *
   * 🚨 DE DONDE SALE: el founder entrego un pedido el mismo, de noche, con su auto, y no tuvo como
   *    registrarlo. El sistema solo conocia dos transportes —el cliente retira en una oficina, o un
   *    courier lleva— y faltaba el mas viejo de los tres: lo llevo yo.
   *
   * 🪤 Y NO PASA POR COBERTURA A PROPOSITO. Un courier llega donde tiene agencias; un auto llega
   *    donde uno decida manejar. Filtrarlo por zonas seria inventarle un limite que no existe, y
   *    obligaria a configurar el reparto propio ANTES de poder usarlo — que es exactamente la
   *    friccion que dejo aquel pedido sin registrar: a las diez de la noche nadie entra a Ajustes.
   *
   * 🪤 Va al FINAL de la lista: es el que menos se usa, y ponerlo primero lo volveria la opcion por
   *    defecto de cualquiera que apure el clic.
   */
  const propio = await cargarRepartoPropio();
  if (propio && !options.some((o) => o.courier_type === 'propia')) options = [...options, propio];

  const recommended = options.find((o) => o.is_auto_assign) ?? options[0] ?? null;
  const recommended_service_id = recommended ? recommended.courier_service_id : null;

  const result: ResolveCouriersResult = { options, recommended_service_id };
  setCached(tenantId, destKey, result);
  return result;
}

/**
 * A qué couriers se les pregunta: los de la plataforma, y los de ESTE comercio.
 *
 * 🚨 ANTES SE PEDÍAN SÓLO LOS DEL SISTEMA, y por eso ponerle dueño a un courier se lo sacaba también
 * al dueño. El founder pidió que Nómades Lurín apareciera sólo para Dulti; sin esta mitad, Dulti
 * habría perdido su propio repartidor por arreglar el de los demás.
 *
 * 🪤 ES UNA FUNCIÓN Y NO UNA LÍNEA SUELTA para poder probar la regla sin ir a la base. La primera
 * versión de su prueba leía el CÓDIGO FUENTE del resolvedor y el trinquete la rebotó, con razón: una
 * prueba que mira el texto pasa igual aunque la consulta nunca se ejecute.
 */
export function aQuienesSeLesPregunta(tenantId: string): string {
  return `and(is_system.eq.true,tenant_id.is.null),tenant_id.eq.${tenantId}`;
}

export interface LoQueLlegaAlDestino {
  /** Qué llega acá: `courier:<id>` y, cuando la cobertura es de un servicio, `servicio:<id>`. */
  cubiertos: Set<string>;
  /** Quiénes declararon su alcance en general. Los que no, se ofrecen igual. */
  declaran: Set<string>;
}

/** Las llaves de lo que llega a este destino, según las filas de cobertura que le calzan. */
export function cubrenEsteDestino(
  filas: ReadonlyArray<{ courier_id: string; courier_service_id?: string | null }>,
): Set<string> {
  const llaves = new Set<string>();
  for (const f of filas) {
    /* 🪤 Una fila SIN servicio vale para todos los del courier: es lo que significan las filas que
       ya existían antes de que la cobertura pudiera apuntar a un servicio. */
    if (f.courier_service_id) llaves.add(`servicio:${f.courier_service_id}`);
    else llaves.add(`courier:${f.courier_id}`);
  }
  return llaves;
}

/** Un courier de la plataforma o del comercio, con sus servicios. */
export interface CourierDelCatalogo {
  id: string;
  name: string;
  type: string;
  logo_url: string | null;
  brand_color: string | null;
  logistics_courier_services: Array<{ id: string; name: string; delivery_type: string }> | null;
}

/**
 * De los couriers de la plataforma y los del comercio, los que llegan al destino, sin los que el comercio apagó.
 * Para cada uno, un servicio representativo (se prefiere la agencia). `alcance` null: el destino todavía no se
 * sabe, y no se filtra por cobertura.
 */
export function losQueLleganAlDestino(
  couriers: ReadonlyArray<CourierDelCatalogo>,
  disabledIds: Set<string>,
  alcance: LoQueLlegaAlDestino | null,
): AvailableCourierOption[] {
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * 🚨 LOS DE LA PLATAFORMA **Y** LOS DEL COMERCIO
   * ═══════════════════════════════════════════════════════════════════════════════════════════
   * Acá se pedían sólo los del sistema. El founder lo vio al revés: «Nómades sólo debe aparecer
   * para Dulti» — Nómades Lurín no es un courier de la plataforma, es el repartidor de una tienda,
   * y estaba cargado como si fuera Shalom.
   *
   * La corrección de fondo es del DATO: ese courier pasa a tener dueño. Pero sin esta línea, el
   * dueño tampoco lo vería: el respaldo se quedaba con los del sistema y a Dulti le habríamos
   * sacado su propio repartidor por arreglar el de los demás.
   *
   * 🪤 Y NO HACE FALTA UNA PANTALLA DE SÚPER ADMIN. Un courier de plataforma lo ven todos; uno de
   * un comercio lo ve ese comercio. Prender y apagar nueve couriers en cada tienda sería resolver
   * a mano, y de nuevo con cada comercio nuevo, algo que el dueño resuelve solo.
   */
  return couriers
    .filter((c) => !disabledIds.has(c.id))
    .flatMap((c) => {
      const services = c.logistics_courier_services ?? [];
      /*
       * 🪤 SE ELIGE ENTRE LOS QUE LLEGAN, y recién después se prefiere la agencia. Al revés, un
       * courier cuya agencia no cubre este destino pero cuya moto sí quedaría fuera de la lista
       * por haber elegido primero el servicio equivocado.
       */
      const llegan = alcance ? services.filter((s) => (
        alcance.cubiertos.has(`servicio:${s.id}`) || alcance.cubiertos.has(`courier:${c.id}`)
      )) : services;
      /* El que no declaró su alcance se ofrece igual: no se le inventa un límite. */
      const candidatos = llegan.length > 0 || !alcance ? llegan : (alcance.declaran.has(c.id) ? [] : services);
      const svc = candidatos.find((s) => s.delivery_type === 'agency') ?? candidatos[0];
      if (!svc) return [];
      return [{
        courier_service_id: svc.id,
        courier_id: c.id,
        courier_name: c.name,
        courier_type: c.type,
        service_name: svc.name,
        delivery_type: svc.delivery_type,
        priority: 0,
        is_auto_assign: false,
        logo_url: c.logo_url,
        brand_color: c.brand_color,
      }];
    });
}

/**
 * Resuelve couriers disponibles para un punto (lat, lng) usando coberturas tipo polígono.
 * Útil para motorizados en Lima o Fenix provincia cuando la dirección está geocodificada.
 */
export async function resolveCouriersForPoint(
  tenantId: string,
  lat: number,
  lng: number
): Promise<ResolveCouriersResult> {
  const { data: coverageRows, error: covError } = await supabase
    .from('logistics_courier_coverage')
    .select('courier_id, polygon_geojson')
    .eq('coverage_type', 'polygon')
    .not('polygon_geojson', 'is', null);

  if (covError || !coverageRows?.length) {
    return { options: [], recommended_service_id: null };
  }

  const courierIdsInPolygon = new Set<string>();
  for (const row of coverageRows as { courier_id: string; polygon_geojson: unknown }[]) {
    const ring = normalizePolygonRing(row.polygon_geojson);
    if (ring && pointInPolygon(lat, lng, ring)) {
      courierIdsInPolygon.add(row.courier_id);
    }
  }

  if (courierIdsInPolygon.size === 0) {
    return { options: [], recommended_service_id: null };
  }

  const { data: rulesRows, error: rulesError } = await supabase
    .from('logistics_shipping_rules')
    .select(`
      id, courier_service_id, priority, is_auto_assign,
      logistics_courier_services ( id, name, delivery_type, courier_id, logistics_couriers ( id, name, type, logo_url, brand_color ) )
    `)
    .eq('tenant_id', tenantId)
    .eq('enabled', true)
    .overrideTypes<ShippingRulePointRow[], { merge: false }>();

  if (rulesError || !rulesRows?.length) {
    return { options: [], recommended_service_id: null };
  }

  const rules = rulesRows;

  const rulesWithCoverage = rules.filter((r) =>
    courierIdsInPolygon.has(r.logistics_courier_services?.courier_id ?? '')
  );

  let options: AvailableCourierOption[] = rulesWithCoverage
    .sort((a, b) => a.priority - b.priority)
    .map((r) => {
      const svc = r.logistics_courier_services;
      const courier = svc?.logistics_couriers;
      return {
        courier_service_id: r.courier_service_id,
        courier_id: svc?.courier_id ?? '',
        courier_name: courier?.name ?? 'Courier',
        courier_type: courier?.type ?? 'agency',
        service_name: svc?.name ?? '',
        delivery_type: svc?.delivery_type ?? 'agency',
        priority: r.priority,
        is_auto_assign: r.is_auto_assign,
        logo_url: courier?.logo_url ?? null,
        brand_color: courier?.brand_color ?? null,
      };
    })
    .filter((o) => o.courier_id);

  const courierIds = [...new Set(options.map((o) => o.courier_id))];
  if (courierIds.length > 0) {
    const { data: disabledRows } = await supabase
      .from('logistics_courier_settings')
      .select('courier_id')
      .eq('tenant_id', tenantId)
      .eq('enabled', false)
      .in('courier_id', courierIds);
    const disabledIds = new Set((disabledRows ?? []).map((r: { courier_id: string }) => r.courier_id));
    options = options.filter((o) => !disabledIds.has(o.courier_id));
  }

  const recommended = options.find((o) => o.is_auto_assign) ?? options[0] ?? null;
  return {
    options,
    recommended_service_id: recommended ? recommended.courier_service_id : null,
  };
}
